import React from 'react'
import {useParams} from 'react-router-dom'
import {useSolv, useTenant} from "./components/SolvProvider";
import {useIntlEx} from "./components/IntlUtils.js"
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  DropdownMenu,
  DropdownMenuItem,
  FormBody,
  FormHeader,
  MainContainer
} from "./components/FormComps.js"
import {AccountStatusLabel, AccountTabs, useResellerType} from "./components/AccountUtils"
import {UserLink} from "./components/UserUtils";
import {DataTable, useCols} from "./components/TableComps"
import {openDialog, openDialogCurried} from "./components/DialogUtils";
import getLogger from "./components/Logging.js"
import './App.css'
import ExportToFileDialog from "./components/ExportToFileDialog";
import AffiliateInviteDialog from "./AffiliateInviteDialog";

const log = getLogger("Affiliates")

export default function Affiliates(props) {

  const params = useParams()

  const {api, session} = useSolv()
  const {intl} = useIntlEx()

  const {tenant} = useTenant(params.tenantId)

  const {resellerTypeOpts, getListableResellerTypeOpts} = useResellerType()

  const {cols} = useCols({
    specs: [
      {
        field: "tenantName",
        label: intl.msg("account"),
        filter: "text",
        sort: "ASC",
        align: "left",
        width: "md",
      },
      {
        field: "ownerDisplayName",
        label: intl.msg("owner"),
        filter: "text",
        sort: "",
        width: "md",
        align: "left"
      },
      {
        field: "status",
        label: intl.msg("status"),
        filter: {
          type: "select:accountStatus",
          multi: true,
          searchable: false,
        },
        sort: "",
        align: "left",
        width: "sm",
      },
    ],
    storageItemName: "resellers",
  })

  function handleFetchDataPage({pageParam: cursor}) {
    return api.listAffiliates({tenantId: tenant.tenantId, extraParams: cols.stringify(), cursor: cursor})
  }

  function handleRenderDataCol(col, row) {
    let n
    switch (col.field) {
      case "tenantName":
        n = <span>{row.tenantName}</span>
        break
      case "ownerDisplayName":
        n = <div className="d-flex">
          {
            row.status !== "INVITING" ? (
              <UserLink user={row.owner} session={session} tenant={row}/>
            ) : (
              row.owner.displayName
            )
          }
        </div>
        break
      case "status":
        n = AccountStatusLabel(row)
        break
    }
    return (
      n
    )
  }

  function canAdd() {
    return tenant && session && session.user && (session.user.isSystemUser() && session.user.isManagerOrAbove())
  }

  function canExport() {
    return session && session.user && (session.user.isSystemUser() && session.user.isReporterOrAbove())
  }

  function handleRowClick(row) {
    window.location = `/${row.tenantId}`
  }

  async function handleExport() {
    return api.exportAffiliates({tenantId: tenant.tenantId, extraParams: cols.stringify(), limit: -1})
  }

  function FormControls(props) {
    const ctls = []

    if (canAdd()) {
      ctls.push(<ActionButton name="add_new" flavor="primary" onClick={() => openDialog("dlg_affiliate_invite")}/>)
    }

    ctls.push(<MoreMenu/>)

    return ctls
  }

  function MoreMenu(props) {

    const ctls = []

    if (canExport()) {
      ctls.push(
        <DropdownMenuItem name="export" onClick={openDialogCurried("dlg_affiliate_export")} prefixWithDivider={ctls.length > 0}/>
      )
    }

    return (
      ctls.length > 0 ? (
        <DropdownMenu>
          {ctls}
        </DropdownMenu>
      ) : (
        <></>
      )
    )
  }

  return (
    tenant &&
      <>
        <MainContainer tenant={tenant} menu={MainMenu} allowIf={(tenant.isSystem()) || (tenant.isReseller() && tenant.hasSubTenants())}>
          <FormHeader>
            <AccountTabs tenant={tenant}/>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                {intl.msg("affiliates")}
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                <FormControls/>
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
          </FormHeader>
          <FormBody>
            <DataTable
              tenant={tenant}
              cols={cols}
              resourceName="affiliates"
              dataKey="tenantId"
              onFetchDataPage={handleFetchDataPage}
              onRenderDataCol={handleRenderDataCol}
              onClickDataRow={handleRowClick}
            />
          </FormBody>
        </MainContainer>
        <AffiliateInviteDialog tenant={tenant}/>
        <ExportToFileDialog
          key="download"
          type="affiliates"
          fileName={`solv-affiliates`}
          fileExt={"csv"}
          onExport={handleExport}/>
      </>
  )
}